import { Form, Flex, Input, InputNumber, Button, Select, Tag } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useEnumsLoader } from '../../../../utils/useEnumsLoader';
import { deleteWordingField } from '../utils/deleteWordingField';

const { TextArea } = Input;
const { Option } = Select;

/**
 * Custom hook for handling credit note fields in a form.
 *
 * @param {Object} options - Options for the hook.
 * @param {Array} options.wordings - List of wordings.
 * @param {Function} options.t - Translation function.
 * @param {Function} options.setWordingTotalHT - Setter for total HT of wordings.
 * @param {Function} options.setWordingTotalTVA - Setter for total TVA of wordings.
 * @param {Function} options.setWordings - Setter for wordings.
 * @param {Object} options.invoice - Invoice object.
 * @param {Function} options.setWordingTitle - Setter for wording title.
 * @param {Object} options.wordingTitle - Wording title object.
 * @param {Object} options.wordingsIds - Wording IDs object.
 * @param {Function} options.setWordingsIds - Setter for wording IDs.
 * @param {Function} options.setUpdatedWordingType - Setter for updated wording type.
 * @param {Function} options.setWordingTotalTTC - Setter for total TTC of wordings.
 * @returns {Object} - Object containing credit note fields.
 */
export const useCreditNoteFields = ({
  wordings,
  t,
  setWordingTotalHT,
  setWordingTotalTVA,
  setWordings,
  invoice,
  setWordingTitle,
  wordingTitle,
  wordingsIds,
  setWordingsIds,
  setUpdatedWordingType,
  setWordingTotalTTC
}) => {
  const { enums, isFieldsLoading } = useEnumsLoader('/customerinvoices/enums');

  const addWordingField = () => {
    const newWording = { id: Date.now().toString(), value: '' };
    setWordings([...wordings, newWording]);
  };

  const creditNoteFields = [
    {
      name: ['type'],
      rules: [{ required: true }],
      input: (
        <Select loading={isFieldsLoading} disabled>
          {(enums?.type || []).map((type) => (
            <Option key={type} value={type}>
              <Tag>{t(`customerinvoices.tags.${type}`)}</Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      noLabel: true,
      name: ['wordings'],
      input: (
        <>
          {wordings.map((wording) => (
            <Flex
              key={wording.id}
              gap={8}
              align="center"
              style={{ marginTop: '8px' }}
            >
              <Form.Item
                name={['wordings', wording.id]}
                noStyle
                rules={[{ required: true }]}
              >
                <Form.Item
                  name={['wordings', wording.id, 'title']}
                  label={t('interninvoices.form.wordings.title')}
                  style={{ width: '100%' }}
                >
                  <Select
                    loading={isFieldsLoading}
                    onChange={(el, option) => {
                      setWordingTitle((prevTotalHT) => ({
                        ...prevTotalHT,
                        [wording.id]: el
                      }));
                      setWordingsIds((prevWordingId) => ({
                        ...prevWordingId,
                        [wording.id]: option.wording._id
                      }));
                    }}
                  >
                    {(
                      invoice?.wordings
                        .filter(
                          (el) =>
                            !Object.values(wordingTitle).includes(el.title)
                        )
                        .filter((el) => !el.credit_note) || []
                    ).map((element) => (
                      <Option
                        key={element.title}
                        value={element.title}
                        wording={element}
                      >
                        {element.title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name={['wordings', wording.id, 'total_ht']}
                  label={t('interninvoices.form.wordings.total_ht')}
                  style={{ width: '100%' }}
                >
                  <InputNumber
                    onChange={(e) => {
                      setUpdatedWordingType('HT');
                      setWordingTotalHT((prevTotalHT) => ({
                        ...prevTotalHT,
                        [wording.id]: e
                      }));
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name={['wordings', wording.id, 'tva']}
                  label={t('interninvoices.form.wordings.tva')}
                  style={{ width: '100%' }}
                >
                  <InputNumber
                    onChange={(e) =>
                      setWordingTotalTVA((prevTotalTVA) => ({
                        ...prevTotalTVA,
                        [wording.id]: e
                      }))
                    }
                  />
                </Form.Item>

                <Form.Item
                  name={['wordings', wording.id, 'total_ttc']}
                  label={t('interninvoices.form.wordings.total_ttc')}
                  style={{ width: '100%' }}
                >
                  <InputNumber
                    onChange={(e) => {
                      setUpdatedWordingType('TTC');
                      setWordingTotalTTC((prevTTC) => ({
                        ...prevTTC,
                        [wording.id]: e
                      }));
                    }}
                  />
                </Form.Item>
              </Form.Item>
              <Button
                type="link"
                danger
                onClick={() =>
                  deleteWordingField({
                    id: wording.id,
                    setWordingTitle,
                    setWordings,
                    wordingTitle,
                    wordings,
                    wordingsIds
                  })
                }
                icon={<DeleteOutlined />}
              />
            </Flex>
          ))}
          <Button
            type="dashed"
            onClick={addWordingField}
            icon={<PlusOutlined />}
            style={{ width: '100%', marginTop: '8px' }}
          >
            {t('interninvoices.form.wordings_add_button')}
          </Button>
        </>
      )
    },
    {
      name: ['means_of_reimbursement']
    },
    {
      name: ['comments'],
      input: <TextArea />
    }
  ];

  return { creditNoteFields };
};
